import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Bell, List, RefreshCw, Phone } from 'react-feather';
import * as AppConstants from '../AppConstants';
import { PropertyActionsModal } from './modals/PropertyActionsModal';

export const Tasks = () => {
  const [taskCount, setTaskCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [taskTypes, setTaskTypes] = useState([{ id: 0, label: 'All' }]);
  const [selectedTaskType, setSelectedTaskType] = useState(0);
  const [error, setError] = useState('');
  const [typesError, setTypesError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [visibleCount, setVisibleCount] = useState(12);
  const currentYear = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${currentYear}-01-01`);
  const [toDate, setToDate] = useState(`${currentYear}-12-31`);
  const loadMoreRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyModalId, setPropertyModalId] = useState(null);
  const [propertyModalTodoId, setPropertyModalTodoId] = useState(null);
  const [whatsappUrlMap, setWhatsappUrlMap] = useState({});
  const whatsappUrlCache = useRef({});

  const fetchTaskCount = useCallback(async () => {
    const staffId = localStorage.getItem('staffMemberId');
    if (!staffId) {
      setError('No staff member selected. Please sign in again.');
      setTaskCount(0);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await axios.get(`/TodoList/MyTasksCount/${staffId}`);
      const value = response?.data;
      const parsedCount =
        typeof value === 'number' ? value : Number(value ?? 0);
      setTaskCount(Number.isFinite(parsedCount) ? parsedCount : 0);
    } catch (err) {
      setError('Could not refresh tasks right now.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTaskTypes = useCallback(async () => {
    const staffInfoRaw = localStorage.getItem('staffMemberInfo');
    let branchId = null;
    try {
      branchId = JSON.parse(staffInfoRaw || '{}')?.branchAccess?.[0]?.id;
    } catch (e) {
      branchId = null;
    }

    if (!branchId) {
      branchId = 1; // fallback branch id
    }

    setIsLoadingTypes(true);
    setTypesError('');
    try {
      const response = await axios.get(`/todoList/GetToDoListViewModel/${branchId}`);
      const incomingTypes =
        response?.data?.taskTypes ||
        response?.data?.TaskTypes ||
        [];
      const mappedTypes = Array.isArray(incomingTypes)
        ? incomingTypes.map((type, index) => ({
            id:
              type.id ??
              type.taskTypeId ??
              type.TaskTypeId ??
              type.value ??
              index + 1,
            label:
              type.description ??
              type.name ??
              type.taskType ??
              type.label ??
              'Task type',
            disallowManualCancellation:
              type.disallowManualCancellation ??
              type.DisallowManualCancellation ??
              false,
            raw: type,
          }))
        : [];

      setTaskTypes([{ id: 0, label: 'All' }, ...mappedTypes]);
    } catch (err) {
      setTypesError('Could not load task types right now.');
      setTaskTypes([{ id: 0, label: 'All' }]);
    } finally {
      setIsLoadingTypes(false);
    }
  }, []);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('en-GB');
    } catch (e) {
      return '-';
    }
  };

  const cacheWhatsappUrl = useCallback((recordId, url) => {
    if (!recordId || !url) return;
    whatsappUrlCache.current[recordId] = url;
    setWhatsappUrlMap((prev) =>
      prev[recordId] === url ? prev : { ...prev, [recordId]: url },
    );
  }, []);

  const getCachedWhatsappUrl = useCallback(
    (recordId) => {
      if (!recordId) return null;
      return whatsappUrlCache.current[recordId] || whatsappUrlMap[recordId] || null;
    },
    [whatsappUrlMap],
  );

  const normalizePhone = (value, keepPlus) => {
    if (!value) return null;
    const cleaned = String(value).replace(
      keepPlus ? /[^\d+]/g : /[^\d]/g,
      '',
    );
    return cleaned || null;
  };

  const extractPhoneFromWhatsappUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const phoneParam = parsed.searchParams.get('phone');
      if (phoneParam) {
        return normalizePhone(phoneParam, true);
      }
      if (parsed.hostname === 'wa.me') {
        const raw = parsed.pathname.replace(/^\/+/, '');
        return normalizePhone(raw, true);
      }
    } catch (e) {
      const match = url.match(/[?&]phone=([^&]+)/);
      if (match) {
        const raw = decodeURIComponent(match[1]);
        return normalizePhone(raw, true);
      }
      const pathMatch = url.match(/wa\.me\/(\d+)/i);
      if (pathMatch) {
        return normalizePhone(pathMatch[1], true);
      }
    }
    return null;
  };

  const extractWhatsappText = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return (
        parsed.searchParams.get('text') ||
        parsed.searchParams.get('message') ||
        parsed.searchParams.get('msg') ||
        null
      );
    } catch (e) {
      const match = url.match(/[?&](text|message|msg)=([^&]+)/i);
      if (!match) return null;
      return decodeURIComponent(match[2]);
    }
  };

  const isMobileDevice = () => {
    if (typeof navigator === 'undefined') return false;
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      return navigator.userAgentData.mobile;
    }
    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
    return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  };

  const getWhatsappLaunchUrl = (url) => {
    if (!url) return null;
    const phone = extractPhoneFromWhatsappUrl(url);
    if (!phone) return url;
    if (!isMobileDevice()) return url;
    const digits = normalizePhone(phone, false);
    if (!digits) return url;
    const text = extractWhatsappText(url);
    const textParam = text ? `?text=${encodeURIComponent(text)}` : '';
    return `https://wa.me/${digits}${textParam}`;
  };

  const getCallHref = (url) => {
    const phone = extractPhoneFromWhatsappUrl(url);
    if (!phone) return null;
    const dialNumber = phone.startsWith('+') ? phone : `+${phone}`;
    return `tel:${dialNumber}`;
  };

  const getWhatsappUrl = useCallback(async (recordId) => {
    if (!recordId) return null;
    if (whatsappUrlCache.current[recordId]) {
      return whatsappUrlCache.current[recordId];
    }
    try {
      const response = await axios.get(`/todoList/GetWhatsappMessageUrl/${recordId}`);
      const url = response?.data;
      if (url) {
        cacheWhatsappUrl(recordId, url);
        return url;
      }
    } catch (e) {
      // Silent failure; button will simply do nothing
    }
    return null;
  }, [cacheWhatsappUrl]);

  const isMobile = isMobileDevice();

  useEffect(() => {
    if (!tasks.length) return;
    const pendingIds = tasks
      .slice(0, visibleCount)
      .filter((task) => task.whatsappNotification || task.WhatsappNotification)
      .map((task) => task.id || task.Id || task.recordId || task.RecordId)
      .filter((id) => id && !whatsappUrlCache.current[id]);

    if (!pendingIds.length) return;

    let cancelled = false;

    const prefetchWhatsappUrls = async () => {
      await Promise.all(
        pendingIds.map(async (id) => {
          const url = await getWhatsappUrl(id);
          if (cancelled || !url) return;
        }),
      );
    };

    prefetchWhatsappUrls();

    return () => {
      cancelled = true;
    };
  }, [tasks, visibleCount, getWhatsappUrl]);

  const handleView = (task) => {
    setModalTask(task);
    setShowModal(true);
  };

  const isPropertyTask = (task) =>
    Number(task.documentContextId || task.DocumentContextId) === AppConstants.DocumentContextId_Property;

  const openPropertyModal = (task) => {
    const propertyId = task.recordId || task.RecordId;
    const todoId = task.id || task.Id;
    if (!propertyId) return;
    setPropertyModalId(propertyId);
    setPropertyModalTodoId(todoId || null);
    setShowPropertyModal(true);
  };

  const closePropertyModal = async () => {
    const todoId = propertyModalTodoId;
    setShowPropertyModal(false);
    setPropertyModalId(null);
    setPropertyModalTodoId(null);

    if (todoId) {
      try {
        const response = await axios.get(`/TodoList/${todoId}`);
        const statusVal =
          response?.data?.status ??
          response?.data?.Status ??
          response?.data?.taskStatus ??
          response?.data?.TaskStatus;

        const isPendingString = typeof statusVal === 'string' && statusVal === 'TaskPending';
        const isPendingNumeric = typeof statusVal !== 'string' && Number(statusVal) === 0;

        if (!isPendingString && !isPendingNumeric) {
          setTasks((prev) => {
            const updated = prev.filter((t) => (t.id || t.Id) !== todoId);
            if (updated.length !== prev.length) {
              setVisibleCount((count) => Math.min(count, updated.length));
            }
            return updated;
          });
        }
      } catch (e) {
        // silent fail; task list unchanged
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalTask(null);
  };

  const getSubjectText = (documentContextId, recordRef) => {
    const map = {
      1: 'All',
      2: 'Client',
      3: 'Property Listing',
      4: 'Promise Of Sale',
      5: 'Residential Lease',
      6: 'Commercial Lease',
      7: 'Address',
      8: 'Property Amendment',
      9: 'Supplier Order',
      10: 'Staff Leave',
      11: 'Staff Member',
      12: 'Payment Request',
      13: 'Projects',
      14: 'Management Agreement',
      15: 'Staff Reports',
      16: 'System',
    };
    const context = map[Number(documentContextId)] || 'Task';
    if (documentContextId === 7 || documentContextId === 1 || (documentContextId >= 4 && documentContextId <= 16 && documentContextId !== 2 && documentContextId !== 3)) {
      return context;
    }
    if (documentContextId === 2 || documentContextId === 3) {
      return `${context} Ref ${recordRef || ''}`.trim();
    }
    return `${context} ${recordRef || ''}`.trim();
  };

  const getTaskTypeMeta = (taskTypeId) =>
    taskTypes.find((t) => Number(t.id) === Number(taskTypeId));

  const handleComplete = async (task) => {
    const id = task.id || task.Id;
    if (!id) return;

    setIsCompleting(true);
    try {
      const payload = {
        id,
        taskTypeId: task.taskTypeId || task.TaskTypeId,
        expectedCompletionDate:
          task.expectedCompletionDate || task.ExpectedCompletionDate,
        assignedToStaffId: task.assignedToStaffId || task.AssignedToStaffId,
        documentContextId: task.documentContextId || task.DocumentContextId,
        recordId: task.recordId || task.RecordId,
        status: 1,
        reminderText: task.reminderText || task.ReminderText,
      };

      await axios.put(`/todoList/${id}`, payload);

      setTasks((prev) => {
        const updated = prev.filter((t) => (t.id || t.Id) !== id);
        setVisibleCount((count) => Math.min(count, updated.length || 0));
        return updated;
      });
      toast.success('Task marked as completed', {
        position: 'top-center',
        autoClose: 3000,
      });
      closeModal();
    } catch (e) {
      setTasksError('Could not mark task as completed right now.');
    } finally {
      setIsCompleting(false);
    }
  };

  const fetchTasks = useCallback(async () => {
    const staffId = localStorage.getItem('staffMemberId');
    if (!staffId) {
      setTasks([]);
      return;
    }

    setIsFetchingTasks(true);
    setTasksError('');
    try {
      const fromValue = fromDate ? new Date(fromDate) : new Date(currentYear, 0, 1);
      const toValue = toDate ? new Date(toDate) : new Date(currentYear, 11, 31);
      const payload = {
        staffMemberId: Number(staffId),
        taskTypeId: Number(selectedTaskType),
        fromDate: fromValue.toISOString(),
        toDate: toValue.toISOString(),
        onlyPending: true,
      };
      const response = await axios.post(`/todoList/GetToDoListStandAlone`, payload);
      const list = Array.isArray(response?.data) ? response.data : [];

      const sorted = [...list].sort((a, b) => {
        const aDate =
          new Date(a.expectedCompletionDate || a.ExpectedCompletionDate || a.dateTimeRaised || a.DateTimeRaised || 0).getTime() || 0;
        const bDate =
          new Date(b.expectedCompletionDate || b.ExpectedCompletionDate || b.dateTimeRaised || b.DateTimeRaised || 0).getTime() || 0;
        return aDate - bDate; // oldest first
      });

      setTasks(sorted);
      setVisibleCount(12);
    } catch (err) {
      setTasksError('Could not load tasks right now.');
      setTasks([]);
    } finally {
      setIsFetchingTasks(false);
    }
  }, [fromDate, selectedTaskType, toDate]);

  useEffect(() => {
    document.title = 'Tasks';
    fetchTaskCount();
    fetchTaskTypes();
  }, [fetchTaskCount, fetchTaskTypes]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCount((prev) => {
              if (prev >= tasks.length) return prev;
              return Math.min(prev + 12, tasks.length);
            });
          }
        });
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [tasks.length]);

  return (
    <div className="container pt-3">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <div className="card shadow-sm border-0 overflow-hidden task-card">
            <div className="tasks-hero d-flex align-items-center px-4 py-3">
              <div className="tasks-hero-icon d-flex align-items-center justify-content-center">
                <Bell size={22} />
              </div>
              <div className="ml-3">
                <p className="mb-1 text-uppercase text-white-50 small">Pending Tasks</p>
                <h4 className="mb-0 text-white">Follow-up your pending actions.</h4>
              </div>
              <button
                type="button"
                className="btn btn-light btn-sm ml-auto tasks-refresh-btn"
                onClick={() => {
                  fetchTaskCount();
                  fetchTasks();
                }}
              >
                <RefreshCw size={14} className="mr-1" />
                Refresh
              </button>
            </div>

            <div className="p-4">
              <div className="row">
                <div className="col-12 col-md-4 mb-3">
                  <label className="form-label font-weight-bold text-muted small">
                    Task type
                  </label>
                  <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                    <select
                      className="form-control"
                      value={selectedTaskType}
                      onChange={(e) => setSelectedTaskType(Number(e.target.value))}
                      disabled={isLoadingTypes}
                    >
                      {taskTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {isLoadingTypes && (
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: '1.5rem', height: '1.5rem' }}
                      >
                        <span className="sr-only">Loading...</span>
                      </div>
                    )}
                  </div>
                  {typesError && (
                    <small className="text-warning d-block mt-1">{typesError}</small>
                  )}
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label className="form-label font-weight-bold text-muted small">
                    From date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label className="form-label font-weight-bold text-muted small">
                    To date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: '1.75rem', height: '1.75rem' }}>
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Checking your tasks...</span>
                </div>
              ) : (
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="task-count-number d-flex align-items-center justify-content-center">
                    {taskCount ?? 0}
                  </div>
                  <div className="ml-3">
                    <p className="mb-1 text-muted small">Pending tasks assigned to you</p>
                    <h5 className="mb-0">Here's the list of your to-dos.</h5>
                  </div>
                </div>
                </div>
              )}

              {error && (
                <div className="alert alert-warning mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}

              <div className="mt-3">
                {isFetchingTasks ? (
                  <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: '1.75rem', height: '1.75rem' }}>
                      <span className="sr-only">Loading...</span>
                    </div>
                    <span className="text-muted">Loading tasks...</span>
                  </div>
                ) : tasksError ? (
                  <div className="alert alert-warning mb-0" role="alert">
                    {tasksError}
                  </div>
                ) : tasks.length === 0 ? (
                  <p className="text-muted mb-0">No tasks for this filter.</p>
                ) : (
                  tasks.slice(0, visibleCount).map((task) => {
                    const dueDate =
                      task.expectedCompletionDate ||
                      task.ExpectedCompletionDate ||
                      task.dateTimeRaised ||
                      task.DateTimeRaised;
                    const taskTypeName = task.taskTypeName || task.TaskTypeName || 'Task';
                    const reminderHtml =
                      task.reminderText ||
                      task.ReminderText ||
                      task.subject ||
                      task.Subject ||
                      'No details';
                    const hasWhatsApp = task.whatsappNotification || task.WhatsappNotification;

                    const recordId = task.id || task.Id || task.recordId || task.RecordId;
                    const whatsappUrl = hasWhatsApp ? getCachedWhatsappUrl(recordId) : null;
                    const whatsappHref = whatsappUrl
                      ? getWhatsappLaunchUrl(whatsappUrl) || whatsappUrl
                      : null;
                    const callHref = whatsappUrl ? getCallHref(whatsappUrl) : null;

                    return (
                      <div className="task-list-item card mb-3 shadow-sm border-0" key={recordId || `${taskTypeName}-${dueDate}`}>
                        <div className="task-list-bar"></div>
                        <div className="card-body">
                          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                            <div className="w-100">
                              <div className="d-flex align-items-center mb-1 flex-wrap">
                                <span className="task-type-chip mr-2 mb-1 mb-md-0">{taskTypeName}</span>
                                <span className="text-muted small ml-auto">{formatDate(dueDate)}</span>
                              </div>
                              <div
                                className="task-subject"
                                dangerouslySetInnerHTML={{ __html: reminderHtml }}
                              />
                            </div>
                            <div className="mt-3 mt-md-0 d-flex align-items-center text-md-right flex-md-column align-items-md-end" style={{ gap: '8px' }}>
                              <button
                                type="button"
                                className="btn btn-sm nav-icon-button task-action-btn"
                                onClick={() => handleView(task)}
                                title="View task"
                              >
                                <i className="far fa-eye" aria-hidden="true"></i>
                              </button>
                              {isPropertyTask(task) ? (
                                <button
                                  type="button"
                                  className="btn btn-sm nav-icon-button task-action-btn"
                                  onClick={() => openPropertyModal(task)}
                                  title="Property summary"
                                >
                                  <List size={16} />
                                </button>
                              ) : null}
                              {hasWhatsApp ? (
                                <>
                                  {callHref ? (
                                    <a
                                      className="btn btn-sm nav-icon-button task-action-btn"
                                      href={callHref}
                                      aria-label="Call"
                                      title="Call"
                                    >
                                      <Phone size={16} />
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-sm nav-icon-button task-action-btn"
                                      disabled
                                      aria-label="Call"
                                      title="Call"
                                    >
                                      <Phone size={16} />
                                    </button>
                                  )}
                                  {whatsappHref ? (
                                    <a
                                      className="btn btn-sm nav-icon-button task-action-btn whatsapp-btn"
                                      href={whatsappHref}
                                      target={isMobile ? undefined : '_blank'}
                                      rel={isMobile ? undefined : 'noopener noreferrer'}
                                      aria-label="Send WhatsApp"
                                      title="Send WhatsApp"
                                    >
                                      <i className="fab fa-whatsapp" aria-hidden="true"></i>
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-sm nav-icon-button task-action-btn whatsapp-btn"
                                      disabled
                                      aria-label="Send WhatsApp"
                                      title="Send WhatsApp"
                                    >
                                      <i className="fab fa-whatsapp" aria-hidden="true"></i>
                                    </button>
                                  )}
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={loadMoreRef}></div>
              </div>

              
            </div>
          </div>
        </div>
      </div>

              {showModal && modalTask && (
                <div className="task-modal-backdrop" onClick={closeModal}>
                  <div
                    className="task-modal card shadow-lg"
                    onClick={(e) => e.stopPropagation()}
          >
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div>
                  <p className="text-muted small mb-1">{modalTask.taskTypeName || modalTask.TaskTypeName || 'Task'}</p>
                  <h5 className="mb-0">
                    {getSubjectText(
                      modalTask.documentContextId || modalTask.DocumentContextId,
                      modalTask.recordRef || modalTask.RecordRef,
                    )}
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn btn-sm nav-icon-button task-action-btn"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              </div>

              <div className="mb-3">
                <p className="text-muted small mb-1">Due date</p>
                <div className="fw-bold">{formatDate(modalTask.expectedCompletionDate || modalTask.ExpectedCompletionDate)}</div>
              </div>

              <div className="mb-3">
                <p className="text-muted small mb-1">Reminder</p>
                <div className="task-modal-reminder" dangerouslySetInnerHTML={{ __html: modalTask.reminderText || modalTask.ReminderText || '—' }} />
              </div>

              {(() => {
                const meta = getTaskTypeMeta(modalTask.taskTypeId || modalTask.TaskTypeId);
                const allowManual = meta ? meta.disallowManualCancellation === false : true;
                return allowManual ? (
                  <button
                    type="button"
                    className="btn login-submit-btn login-submit-btn--wide w-100"
                    onClick={() => handleComplete(modalTask)}
                    disabled={isCompleting}
                  >
                    {isCompleting ? 'Completing...' : 'Mark as completed'}
                  </button>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}
      <PropertyActionsModal
        show={showPropertyModal}
        propertyId={propertyModalId}
        originPath="/Tasks"
        onClose={closePropertyModal}
      />
    </div>
  );
};
